import { useEffect, useMemo, useRef, useState } from 'react';
import clock from '~/assets/icons/clock.svg';
import { CollectionImage } from '~/components/collections';
import { Text } from '~/components/ui/Text';
import { formatTime, numWord } from '~/shared';
import { banners } from './CasePromotionBanner.model';
import {
	StyledCaseImageContainer,
	StyledCaseShadow,
	StyledContainer,
	StyledCount,
	StyledDescContainer,
	StyledProgress,
	StyledProgressWrapper,
	StyledTimerContainer,
	StyledTitle,
} from './CasePromotionBanner.styled';

const caseNumber = 0;

const CasePromotionBanner = () => {
	const timerTextRef = useRef<HTMLDivElement>(null);
	const endsAt = banners[caseNumber].promotionEndsAt;

	const dayMs = 24 * 60 * 60 * 1000;

	const [isTimerVisible, setIsTimerVisible] = useState(() => {
		if (!endsAt) return false;
		return endsAt - Date.now() > 0;
	});

	const initialTimerText = useMemo(() => {
		if (!endsAt) return '00:00';
		const diff = endsAt - Date.now();
		if (diff <= 0) return '00:00';
		if (diff > dayMs) {
			const daysLeft = Math.floor(diff / dayMs);
			return `${daysLeft} ${numWord(daysLeft, ['день', 'дня', 'дней'])}`;
		}
		return formatTime(diff, '{hh}:{mm}') || '00:00';
	}, [endsAt]);

	useEffect(() => {
		if (!endsAt) {
			setIsTimerVisible(false);
			return;
		}

		let intervalId: ReturnType<typeof setInterval> | null = null;

		const update = () => {
			const diff = endsAt - Date.now();
			const visible = diff > 0;
			setIsTimerVisible(visible);

			if (!timerTextRef.current) return;
			if (!visible) {
				if (intervalId) clearInterval(intervalId);
				return;
			}

			if (diff > dayMs) {
				const daysLeft = Math.floor(diff / dayMs);
				timerTextRef.current.textContent = `${daysLeft} ${numWord(daysLeft, [
					'день',
					'дня',
					'дней',
				])}`;
			} else {
				timerTextRef.current.textContent =
					formatTime(diff, '{hh}:{mm}') || '00:00';
			}
		};

		update();
		intervalId = setInterval(update, 1000);
		return () => {
			if (intervalId) clearInterval(intervalId);
		};
	}, [endsAt]);

	return (
		<StyledContainer $caseNumber={caseNumber}>
			<StyledCaseShadow />
			<StyledTitle>
				<span style={{ fontWeight: 700, fontSize: 12, opacity: 0.5 }}>
					{banners[caseNumber].subtitle}
				</span>
				<br />
				<span style={{ fontWeight: 800, fontSize: 40, lineHeight: '56px' }}>
					{banners[caseNumber].title}
				</span>
			</StyledTitle>
			<StyledCount>Осталось {banners[caseNumber].count} шт.</StyledCount>
			<StyledProgressWrapper>
				<StyledProgress
					style={{
						width: `${
							(banners[caseNumber].count / banners[caseNumber].totalCount) * 100
						}%`,
					}}
				/>
			</StyledProgressWrapper>
			<StyledCaseImageContainer>
				<CollectionImage
					collection={'cases'}
					id={banners[caseNumber].caseId ?? '1'}
					size={512}
				/>
			</StyledCaseImageContainer>
			<StyledDescContainer>
				<Text>{banners[caseNumber].description}</Text>
				{isTimerVisible && (
					<StyledTimerContainer>
						<Text>До конца акции</Text>
						<img src={clock} />
						<Text ref={timerTextRef}>{initialTimerText}</Text>
					</StyledTimerContainer>
				)}
			</StyledDescContainer>
		</StyledContainer>
	);
};

export { CasePromotionBanner };
